import React, { useMemo, Fragment, useEffect, useState } from 'react';
import {
    MaterialReactTable,
    MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
    MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
    MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from 'material-react-table';
import Typography from '@mui/material/Typography';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { Link, useNavigate } from "react-router-dom";
import { useViewAccountTeamMembersListQuery } from '../../../reactQueries/AppApi/rqHooksForAccountTeamMembers';
import { dateformatConverter } from "../../../common/utilities/dates/dateformatConverter";
import Stack from "@mui/material/Stack";
import Button from '@mui/material/Button';
import { accountTeamMemberCreatePath, accountTeamMemberEditPath } from "../../../routes/routePaths";
import MpgErrorDialog from "../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../common/tooltips/HtmlTooltip"
import { getAccessTypes } from "../../../hooksAndFunctions/functions/getAccessTypes";
import ExportButton from '../../../common/otherCommonComponents/ExportButton';
import {
    useGetSiteCoreCMSData,
    useTrackActivityMutation,
} from '../../../reactQueries/commonRQHooks';
import { useGetLoggedInUserDetails } from "../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";

const TooltipCell = ({ renderedCellValue }) => (
    <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
    return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewAccountTeamMembers = () => {
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { loggedInUser } = useGetLoggedInUserDetails();
    const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
    const {
        hasA1Access,
        hasA4Access
    } = clientPortalAdminType

    const canExportXls = isEnterpriseAdminType || hasA1Access;
  const { id: userId } = loggedInUser;

    const { isSuccess, isFetching: isFetchingOnAccTeamData, data: newData, isError } = useViewAccountTeamMembersListQuery()

    const { data: adminAccountCMSDataFromAPI } = useGetSiteCoreCMSData({ pagename: "AccountTeam" });

    const [adminAccountTeamCMSData, setAdminAccountTeamCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} });

    //#region  CMS Content hadling

    const fetchAdminAccountTeamCMSData = () => {
        adminAccountCMSDataFromAPI?.Pages?.forEach((el) => {
            if (el.Key === "AccountTeam") {
                const { Title, Description, TabNavigationTitle, Components } = el;
                setAdminAccountTeamCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
            }
        });
    }

    useEffect(() => {
        fetchAdminAccountTeamCMSData();
    }, [adminAccountCMSDataFromAPI])

    const fetchAdminAccountTeamListLabel = (key) => {
        const labels = adminAccountTeamCMSData?.pageContent?.AccountTeamListComponent?.Labels;
        return returnLabel(key, labels);
    }

    const fetchCommonCMSLabel = (key) => {
        const labels = adminAccountTeamCMSData?.pageContent?.CommonComponent?.Labels;
        return returnLabel(key, labels);
    }

   // #endregion

    const accountTeamTitle = adminAccountTeamCMSData?.title;
    const exportFileName = `${accountTeamTitle}.xlsx`;
    const exportWorksheetName = accountTeamTitle;

    const exportColumns = [
        { id: 'fullName', header: fetchAdminAccountTeamListLabel('AccountTeamList.Name'), getValue: (row) => `${row?.FirstName || ''} ${row?.LastName || ''}`.trim() },
        { id: 'EmailAddress', header: fetchAdminAccountTeamListLabel('AccountTeamList.Email'), getValue: (row) => row?.EmailAddress || '' },
        { id: 'JobPosition', header: fetchAdminAccountTeamListLabel('AccountTeamList.JobTitle'), getValue: (row) => row?.JobPosition || '' },
        { id: 'isActive', header: fetchAdminAccountTeamListLabel('AccountTeamList.Status'), getValue: (row) => row?.IsActive ? 'Active' : 'Inactive' },
        { id: 'CreatedBy', header: fetchAdminAccountTeamListLabel('AccountTeamList.CreatedBy'), getValue: (row) => row?.CreatedBy || '' },
        { id: 'createdOn', header: fetchAdminAccountTeamListLabel('AccountTeamList.CreatedOn'), getValue: (row) => row?.CreatedTimestamp ? dateformatConverter(row.CreatedTimestamp, "DD MMM YYYY hh:mm A") : '' },
    ];

    //should be memoized or stable
    const columns = useMemo(
        () => [
            {
                accessorFn: (row) => `${row.FirstName} ${row.LastName}`,
                id: 'fullName',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.Name'),
                Cell: TooltipCell,
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderName") }
            },
            {
                accessorKey: 'EmailAddress',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.Email'),
                Cell: TooltipCell,
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderEmail") }
            },
            {
                accessorKey: 'JobPosition',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.JobTitle'),
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderJobTitle") }
            },
            {
                accessorFn: (originalRow) => {
                    const val = originalRow.IsActive
                    return val ? "Active" : "Inactive"
                }, //must be strings
                id: 'isActive',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.Status'),
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderStatus") }
            },
            {
                accessorKey: 'CreatedBy',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.CreatedBy'),
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderCreatedBy") }
            },
            {
                accessorFn: (originalRow) => {
                    const data = dateformatConverter(originalRow.CreatedTimestamp, "DD MMM YYYY hh:mm A");
                    return `${data}`
                },
                id: 'createdOn',
                header: fetchAdminAccountTeamListLabel('AccountTeamList.CreatedOn'),
                filterFn: 'contains',
                sortingFn: 'datetime',
                muiTableHeadCellFilterTextFieldProps: { placeholder: fetchAdminAccountTeamListLabel("AccountTeamList.PlaceholderCreatedOn") }
            },
        ],
        [adminAccountTeamCMSData],
    );

    /**redirect hook */
    let navigate = useNavigate();

    return (
        <Fragment>
            {
                !!isFetchingOnAccTeamData && fetchCommonCMSLabel('Common.Waitloading')
            }
            <Typography variant="h5" align='center' sx={{ fontWeight: 'bolder' }}>
                {adminAccountTeamCMSData?.title}
            </Typography>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                m={1}
            >
                {
                    !hasA4Access && (
                        <Button
                            variant="outlined"
                            size="small"
                            component={Link}
                            to={accountTeamMemberCreatePath}
                        >{fetchAdminAccountTeamListLabel('AccountTeamList.AddAccountTeamMember')}</Button>
                    )
                }

            </Stack>

            {/**incase successful call but empty response */}
            {!!isSuccess && !newData?.length && (<NoTableDataComp />)}

            {
                !!isSuccess && !!newData?.length && (

                    <MaterialReactTable
                        columns={columns}
                        data={newData || []}
                        layoutMode="grid"
                        enableGlobalFilter={false}
                        enableDensityToggle={false}
                        initialState={{
                            density: 'compact',
                            showColumnFilters: true
                        }}
                        state={{
                            isLoading: isFetchingOnAccTeamData,
                            columnPinning: {
                                left: ['mrt-row-actions', 'fullName']
                            }
                        }}
                        enableStickyHeader
                        enableRowActions
                        displayColumnDefOptions={{
                            'mrt-row-actions': {
                                header: fetchAdminAccountTeamListLabel('AccountTeamList.Actions'),
                            },
                        }}
                        muiTablePaginationProps={{
                            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
                        }}
                       renderToolbarInternalActions={({ table }) => (
                                 <>
                                   {canExportXls && (
                                       <ExportButton
                                           table={table}
                                           tooltipTitle={fetchAdminAccountTeamListLabel("AccountTeamList.ExportToXLS")}
                                           trackActivity={trackActivityMutate}
                                           userId={userId}
                                           exportColumns={exportColumns}
                                           fileName={exportFileName}
                                           worksheetName={exportWorksheetName}
                                       />
                                   )}
                                   <MRTToggleFiltersButton table={table} />
                                   <MRTShowHideColumnsButton table={table} />
                                   <MRTFullScreenToggleButton table={table} />
                                 </>
                               )}
                        renderRowActions={({ row, table }) => {
                            const staffId = row.original.Id
                            return (
                                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                                    <IconButton
                                        onClick={() => {
                                            navigate(accountTeamMemberEditPath({ id: staffId }))
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Box>
                            )

                        }}
                    />
                )}

            <MpgErrorDialog
                open={isError}
            />
        </Fragment>

    );
};

export default ViewAccountTeamMembers;
